/**
 * HomePage.jsx - Main Landing Page Component
 * 
 * This is the main landing page of the pest control service website.
 * It consists of multiple sections including:
 * - Hero section with call-to-action
 * - Services overview
 * - Why choose us section
 * - About pest control information
 * - Image gallery
 * - Quick contact form
 * 
 * Dependencies:
 * - react: Core React library
 * - react-router-dom: For client-side navigation
 * - framer-motion: For smooth animations
 * - bootstrap: For responsive layout and components
 * - react-icons: For decorative icons
 * - react-helmet-async: For SEO optimization
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom'; // For client-side navigation
import { motion } from 'framer-motion'; // For animations
import { Helmet } from 'react-helmet-async'; // For SEO optimization

// Import styles
import './HomePage.css'; // Page-specific styles

// Import images
import heroBackground from '../assets/Images/p-1.png';

// Import service images
import termiteImg from '../assets/Images/general-pest-control-images/Termites.jpg';
import cockroachImg from '../assets/Images/general-pest-control-images/Cockroach.jpeg';
import rodentImg from '../assets/Images/general-pest-control-images/Rodent.jpeg';
import bedBugImg from '../assets/Images/general-pest-control-images/Bed Bug.jpeg';
import mosquitoImg from '../assets/Images/general-pest-control-images/Mosquito.jpeg';
import antImg from '../assets/Images/general-pest-control-images/Ant.jpg';
import generalPestImg from '../assets/Images/general-pest-control-images/General_Pest_Control.png';
import lizardImg from '../assets/Images/general-pest-control-images/Lizard.jpeg';
import flyImg from '../assets/Images/general-pest-control-images/Fly.jpeg';
import pestGuard from '../assets/Images/p-2.png';
import pestUnderstanding from '../assets/Images/p-3.png';

/**
 * PlaceholderIcon Component
 * 
 * A reusable icon component that renders a bug icon with customizable styling.
 * 
 * @param {string} className - CSS classes to apply to the icon (defaults to 'bi bi-bug-fill icon-red')
 * @returns {JSX.Element} An icon element
 */
const PlaceholderIcon = ({ className = 'bi bi-bug-fill icon-red' }) => (
  <i 
    className={className} 
    style={{ fontSize: '2rem' }} // Set a default size
    aria-hidden="true" // Hide from screen readers as it's decorative
  />
);

/**
 * HeroSection Component
 * 
 * The hero section is the first visual element users see on the page.
 * It includes a full-width background image, a main heading, and a brief description.
 * Uses Framer Motion for smooth entrance animations.
 */
const HeroSection = () => {
  return (
    <motion.div 
      className="hero-section text-center text-white" 
      style={{
        backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.1)), url(${heroBackground})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        position: 'relative'
      }}
      initial={{ opacity: 0 }} // Start invisible
      animate={{ opacity: 1 }}  // Fade in
      transition={{ duration: 0.8 }} // Animation duration
      role="banner" // ARIA landmark
    >
      {/* Container for centering content */}
      <div 
        className="container d-flex flex-column justify-content-center align-items-center" 
        style={{ minHeight: '70vh', position: 'relative', zIndex: 2 }}
      >
        {/* Main heading with animation */}
        <motion.h1 
          className="display-3 fw-bold mb-3"
          initial={{ y: -50, opacity: 0 }} // Start above and invisible
          animate={{ y: 0, opacity: 1 }}   // Move down and fade in
          transition={{ duration: 0.5, delay: 0.3 }} // Animation with delay
        >
          General Pest Control Services Jodhpur
        </motion.h1>
        
        {/* Subheading with animation */}
        <motion.p 
          className="lead mb-4"
          initial={{ y: -30, opacity: 0 }} // Start above and invisible
          animate={{ y: 0, opacity: 1 }}   // Move down and fade in
          transition={{ duration: 0.5, delay: 0.5 }} // Slight delay after heading
        >
          Reliable, Safe, and Affordable Pest Management Solutions.
        </motion.p>
        
        {/* Call-to-action button container with animation */}
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }} // Start scaled down and invisible
          animate={{ scale: 1, opacity: 1 }}   // Scale up and fade in
          transition={{ duration: 0.5, delay: 0.7 }} // Last element to animate
        >
          {/* CTA Button - Currently empty, can be added here */}
        </motion.div>
      </div>
      
      {/* Overlay for better text readability */}
      <div 
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.2)',
          zIndex: 1
        }}
        aria-hidden="true" // Hide from screen readers as it's decorative
      />
    </motion.div>
  );
};

/**
 * ServicesSection Component
 * 
 * Displays a grid of service cards with icons, titles, and descriptions.
 * Each card has a hover animation effect. The section includes a call-to-action
 * button that links to the full services page.
 * 
 * Features:
 * - Responsive grid layout (3 columns on desktop, 2 on tablet, 1 on mobile)
 * - Hover animations using Framer Motion
 * - Consistent card styling with shadow and border
 * - Matches the design from ServicesPage.jsx
 */
const ServicesSection = () => {
  const services = [
    { 
      id: 1, 
      title: 'Termite Control', 
      description: 'Effective solutions to protect your property from destructive termites and ensure long-term prevention.',
      image: termiteImg
    },
    { 
      id: 2, 
      title: 'Cockroach Control', 
      description: 'Targeted treatments to eliminate cockroach populations and sanitize affected areas.',
      image: cockroachImg
    },
    { 
      id: 3, 
      title: 'Rodent Control', 
      description: 'Safe and effective rodent extermination.',
      image: rodentImg
    },
    { 
      id: 4, 
      title: 'Bed Bug Treatment', 
      description: 'Comprehensive bed bug extermination to restore comfort and peace to your bedrooms.',
      image: bedBugImg
    },
    { 
      id: 5, 
      title: 'Mosquito Control', 
      description: 'Reduce mosquito populations around your home.',
      image: mosquitoImg
    },
    { 
      id: 6, 
      title: 'Ant Control', 
      description: 'Eliminate ant colonies and prevent reinfestation.',
      image: antImg
    },
    { 
      id: 7, 
      title: 'General Pest Control', 
      description: 'Comprehensive pest management solutions.',
      image: generalPestImg
    },
    { 
      id: 8, 
      title: 'Lizard Control', 
      description: 'Humane and effective lizard removal services.',
      image: lizardImg
    },
    { 
      id: 9, 
      title: 'Fly Control', 
      description: 'Eliminate flies and prevent breeding sites.',
      image: flyImg
    }
  ];

  return (
    <section className="services-section py-5" id="services">
      <div className="container">
        {/* Section header with title and subtitle */}
        <motion.div
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="text-center mb-5"
        >
          <h2 className="section-title">Our Services</h2>
          <p className="section-subtitle">Tailored pest control solutions for a pest-free environment.</p>
        </motion.div>
        
        {/* Services grid */}
        <div className="row g-4">
          {services.map((service, index) => (
            <motion.div 
              className="col-lg-4 col-md-6" 
              key={service.id}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <div className="card service-card h-100 text-center shadow-sm border-0 hover-shadow transition-all">
                <div className="card-body d-flex flex-column h-100 p-4">
                  <div className="mb-3 d-flex align-items-center justify-content-center flex-grow-1" style={{
                    minHeight: '180px',
                    maxHeight: '200px',
                    width: '100%',
                    overflow: 'hidden',
                    borderRadius: '8px',
                    backgroundColor: '#f8f9fa',
                    padding: '1rem'
                  }}>
                    <img 
                      src={service.image} 
                      alt={service.title}
                      className="img-fluid"
                      style={{
                        maxHeight: '100%',
                        maxWidth: '100%',
                        width: 'auto',
                        height: 'auto',
                        objectFit: 'contain',
                        transition: 'transform 0.3s ease-in-out',
                        borderRadius: '4px'
                      }}
                      onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                      onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                    />
                  </div>
                  <h5 className="card-title fw-bold mt-3 mb-2">{service.title}</h5>
                  <p className="card-text text-muted mb-0">{service.description}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

/**
 * WhyChooseUsSection Component
 * 
 * Highlights the key reasons why customers should choose the company's services.
 * Split into two columns: one with a main message and bullet points, and the other
 * with feature cards. Uses Bootstrap Icons for visual elements.
 * 
 * Features:
 * - Two-column responsive layout
 * - Feature cards with icons
 * - Bullet-pointed benefits list
 * - Consistent styling with the rest of the site
 */
const WhyChooseUsSection = () => {
  // Array of feature objects containing icon, title, and description
  const features = [
    {
      icon: <i className="bi bi-shield-check fs-1" style={{ color: '#dc3545' }} aria-hidden="true"></i>,
      title: 'Certified Experts',
      description: 'Our team consists of certified and experienced pest control professionals.'
    },
    {
      icon: <i className="bi bi-people fs-1" style={{ color: '#dc3545' }} aria-hidden="true"></i>,
      title: 'Customer-First Approach',
      description: 'Your satisfaction is our top priority. We tailor our services to your specific needs.'
    },
    {
      icon: <i className="bi bi-currency-dollar fs-1" style={{ color: '#dc3545' }} aria-hidden="true"></i>,
      title: 'Affordable Pricing',
      description: 'High-quality pest control services at competitive and transparent prices.'
    },
    {
      icon: <i className="bi bi-clock-history fs-1" style={{ color: '#dc3545' }} aria-hidden="true"></i>,
      title: '24/7 Availability',
      description: 'Emergency services available round the clock for your convenience.'
    }
  ];

  return (
    <section className="py-5 bg-light" id="why-choose-us" style={{
      background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
      borderTop: '1px solid rgba(0,0,0,0.05)',
      borderBottom: '1px solid rgba(0,0,0,0.05)'
    }}>
      <div className="container">
        <div className="row align-items-center">
          {/* Left column - Main content */}
          <div className="col-lg-6 mb-4 mb-lg-0">
            <div className="pe-lg-5">
              <h2 className="section-title">Why Choose Us</h2>
              <p className="lead">
                With years of experience and a commitment to excellence, we provide the best pest control solutions in Jodhpur.
              </p>
              
              {/* Benefits list with checkmark icons */}
              <ul className="list-unstyled">
                <li className="mb-2">
                  <i className="bi bi-check-circle-fill me-2" style={{ color: '#dc3545' }} aria-hidden="true"></i>
                  <span>100% Satisfaction Guaranteed</span>
                </li>
                <li className="mb-2">
                  <i className="bi bi-check-circle-fill me-2" style={{ color: '#dc3545' }} aria-hidden="true"></i>
                  <span>Safe & Environmentally Friendly</span>
                </li>
                <li className="mb-2">
                  <i className="bi bi-check-circle-fill me-2" style={{ color: '#dc3545' }} aria-hidden="true"></i>
                  <span>Licensed & Insured Professionals</span>
                </li>
                <li className="mb-2">
                  <i className="bi bi-check-circle-fill me-2" style={{ color: '#dc3545' }} aria-hidden="true"></i>
                  <span>Free Inspection & Quote</span>
                </li>
              </ul>
              
              <Link to="/about" className="btn btn-danger mt-3" aria-label="Learn more about our company">
                Learn More About Us
              </Link>
            </div>
          </div>
          
          {/* Right column - Feature cards */}
          <div className="col-lg-6">
            <div className="row g-4">
              {features.map((feature, index) => (
                <motion.div 
                  key={index} 
                  className="col-md-6"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.5 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <div className="card h-100 border-0 shadow-sm p-4" style={{
                    background: 'white',
                    transition: 'all 0.3s ease',
                    borderLeft: '4px solid #dc3545',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.05)'
                  }} 
                  onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-5px)'} 
                  onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}>
                    <div className="mb-3" aria-hidden="true" style={{ color: '#0d6efd' }}>
                      {feature.icon}
                    </div>
                    <h3 className="h5 fw-semibold mb-3">{feature.title}</h3>
                    <p className="mb-0 text-muted">{feature.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/**
 * AboutUsSection Component
 * 
 * Provides information about the company, its mission, and values.
 * Positioned between Why Choose Us and Professional Pest Control sections.
 * 
 * Features:
 * - Two-column responsive layout
 * - Company information with mission and values
 * - Animated content with Framer Motion
 * - Consistent styling with the rest of the site
 */
const AboutUsSection = () => {
  return (
    <section className="py-5" id="about-us" style={{
      background: '#ffffff',
      borderBottom: '1px solid rgba(0,0,0,0.05)'
    }}>
      <div className="container">
        <div className="row align-items-center">
          {/* Left column - Image */}
          <motion.div 
            className="col-lg-6 mb-4 mb-lg-0"
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <div className="position-relative">
              <img 
                src={pestUnderstanding} 
                alt="Professional pest control team at work" 
                className="img-fluid rounded shadow"
              />
            </div>
          </motion.div>
          
          {/* Right column - Content */}
          <motion.div 
            className="col-lg-6"
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.2 }}
          >
            <div className="ps-lg-5">
              <span className="text-uppercase fw-bold d-block mb-2 text-danger fw-bolder" style={{
      color: '#0d6efd',
      letterSpacing: '1px',
      fontSize: '0.85rem'
      }}> Your Trusted Pest Control Partner</span>
              <h2 className="section-title mb-4">About Us</h2>
              
              <p className="lead text-muted mb-4">
                At <span className="fw-bold">General Pest Control Services Jodhpur</span>, we are committed to providing safe, effective, and environmentally responsible pest management solutions.
              </p>
              
              <div className="mb-4">
                <div className="d-flex mb-3">
                  <div className="me-3">
                    <i className="bi bi-check2-circle fs-4" style={{ color: '#dc3545' }}></i>
                  </div>
                  <div>
                    <h4 className="h5 mb-1">Our Mission</h4>
                    <p className="mb-0">To protect homes and businesses from pests while ensuring the safety of your family, pets, and the environment.</p>
                  </div>
                </div>
              </div>
              
              <Link to="/about" className="btn btn-outline-danger">
                About Us <i className="bi bi-arrow-right ms-2"></i>
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const ownerPhoneNumber = '+919587425595'; // TODO: Replace with actual owner's WhatsApp number
const prefilledMessage = encodeURIComponent("Hello! I'm interested in your pest control services."); // TODO: Customize if needed
const businessEmail = 'Generalpestcontrolservicesjodh@gmail.com'; // Business email address
const contactPhoneNumber = '+919587425595'; // TODO: Replace with actual phone number for calls

/**
 * QuickContactSection Component
 * 
 * Provides quick contact options for customers, including WhatsApp, email, and phone.
 * 
 * Features:
 * - Responsive layout with three columns on desktop and one on mobile
 * - Animated cards with hover effects
 * - Consistent styling with the rest of the site
 * 
 * @returns {JSX.Element} The Quick Contact section component
 */
const QuickContactSection = () => {
  return (
    <section className="quick-contact-section py-5" id="contact">
      <div className="container">
        <div className="text-center mb-5">
          <h2 className="section-title">Get In Touch Quickly</h2>
          <p className="section-subtitle">Choose your preferred method to reach out to us</p>
        </div>
        
        <div className="row g-4 justify-content-center">
          {/* WhatsApp Card */}
          <motion.div 
            className="col-lg-4 col-md-6"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <div className="card h-100 border-0 shadow-sm">
              <div className="card-body text-center p-4">
                <div className="icon-wrapper mb-3">
                  <i className="bi bi-whatsapp fs-1 text-success"></i>
                </div>
                <h3 className="h5">WhatsApp Us</h3>
                <p className="text-muted mb-4">Chat with us directly on WhatsApp for a quick response.</p>
                <a 
                  href={`https://wa.me/${+919587425595}?text=${prefilledMessage}`}
                  className="btn btn-outline-success w-100"
                  target="_blank" 
                  rel="noopener noreferrer"
                  aria-label="Chat with us on WhatsApp"
                >
                  <i className="bi bi-whatsapp me-2"></i>Start Chat
                </a>
              </div>
            </div>
          </motion.div>

          {/* Email Card */}
          <motion.div 
            className="col-lg-4 col-md-6"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <div className="card h-100 border-0 shadow-sm">
              <div className="card-body text-center p-4">
                <div className="icon-wrapper mb-3">
                  <i className="bi bi-envelope-fill fs-1 text-primary"></i>
                </div>
                <h3 className="h5">Email Us</h3>
                <p className="text-muted mb-4">Send us an email with your queries or requirements.</p>
                <a 
                  href={`mailto:${businessEmail}`}
                  className="btn btn-outline-primary w-100"
                  aria-label="Send us an email"
                >
                  <i className="bi bi-envelope-fill me-2"></i>Send Email
                </a>
              </div>
            </div>
          </motion.div>

          {/* Phone Card */}
          <motion.div 
            className="col-lg-4 col-md-6"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <div className="card h-100 border-0 shadow-sm">
              <div className="card-body text-center p-4">
                <div className="icon-wrapper mb-3">
                  <i className="bi bi-telephone-fill fs-1 text-danger"></i>
                </div>
                <h3 className="h5">Call Us</h3>
                <p className="text-muted mb-4">Speak to our team directly for immediate assistance.</p>
                <a 
                  href={`tel:${+919587425595}`}
                  className="btn btn-outline-danger w-100"
                  aria-label="Call us now"
                >
                  <i className="bi bi-telephone-fill me-2"></i>Call Now
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};


/**
 * AboutPestControlSection Component
 * 
 * Provides information about pest control services and their importance.
 * 
 * Features:
 * - Two-column layout with text and image
 * - Responsive design
 * - Animated content with Framer Motion
 * 
 * @returns {JSX.Element} The About Pest Control section component
 */
const AboutPestControlSection = () => {
  return (
    <section className="py-5" id="about-pest-control" style={{
      background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
      borderTop: '1px solid rgba(0,0,0,0.05)'
    }}>
      <div className="container">
        <div className="row align-items-center">
          <motion.div 
            className="col-lg-6 mb-4 mb-lg-0"
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <div className="pe-lg-5">
              <h2 className="section-title">Pest Control Services</h2>
              <p className="lead mb-4">
                Protect your home or business from unwanted pests with our comprehensive pest control solutions.
              </p>
              <p className="mb-4">
                Our experienced team uses safe and effective methods to eliminate pests and prevent future infestations. 
                We understand the importance of a pest-free environment for your health and peace of mind.
              </p>
              <ul className="list-unstyled mb-4">
                <li className="mb-2">
                  <i className="bi bi-check-circle-fill text-success me-2"></i>
                  <span>Eco-friendly pest control solutions</span>
                </li>
                <li className="mb-2">
                  <i className="bi bi-check-circle-fill text-success me-2"></i>
                  <span>Licensed and certified technicians</span>
                </li>
                <li className="mb-2">
                  <i className="bi bi-check-circle-fill text-success me-2"></i>
                  <span>Customized treatment plans</span>
                </li>
                <li className="mb-2">
                  <i className="bi bi-check-circle-fill text-success me-2"></i>
                  <span>Emergency services available</span>
                </li>
              </ul>
              <Link to="/services" className="btn btn-primary">
                View Services
              </Link>
            </div>
          </motion.div>
          <motion.div 
            className="col-lg-6"
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.2 }}
          >
            <div className="position-relative">
              <img 
                src={pestUnderstanding} 
                alt="Professional pest control service" 
                className="img-fluid rounded shadow"
              />
             
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

/**
 * HomePage Component
 * 
 * Main component that composes all the sections of the home page.
 * 
 * @returns {JSX.Element} The complete HomePage component
 */
const HomePage = () => {
  return (
    <>
      <Helmet>
        <title>General Pest Control Services</title>
        <meta name="description" content="General Pest Control Services. We offer reliable, safe, and affordable solutions for home pest protection and extermination." />
        <meta name="keywords" content="pest control services, home pest protection, professional exterminators, termite control, rodent control, insect control, Jodhpur pest control, residential pest control" />
      </Helmet>
      <HeroSection />
      <ServicesSection />
      <WhyChooseUsSection />
      <AboutUsSection />
      <AboutPestControlSection />
      <QuickContactSection />
    </>
  );
};

export default HomePage;
